import { useState } from 'react';
import { formatNumber, convertFuel, toFuelLPerNm, rangeNm, type FuelUnit } from '@boatnav/core';
import { NumberField } from '../../lib/NumberField.js';
import { useBoatStore } from './boatStore.js';
import { useUnitsStore } from '../../app/unitsStore.js';
import { uuid } from '../../lib/uuid.js';
import type { Boat } from '../../lib/db.js';
import { msg } from '../../lib/messages.js';

interface BoatFormProps {
  boat: Boat | null;
  canDelete: boolean;
  onClose: () => void;
}

function blankBoat(fuelUnit: Boat['fuelUnit']): Boat {
  return {
    id: uuid(),
    name: '',
    draughtM: 1.2,
    cruiseKn: 20,
    fuelValue: fuelUnit === 'l_per_h' ? 24 : 1.1,
    fuelUnit,
    tankL: null,
    clearanceM: 0.5,
    updatedAt: Date.now(),
    deletedAt: null,
  };
}

export function BoatForm({ boat, canDelete, onClose }: BoatFormProps): JSX.Element {
  const { save, remove } = useBoatStore();
  const consumptionUnit = useUnitsStore((s) => s.consumption);
  const [draft, setDraft] = useState<Boat>(() => boat ?? blankBoat(consumptionUnit));
  const [saved, setSaved] = useState(false);

  const patch = (p: Partial<Boat>): void => {
    const next = { ...draft, ...p, updatedAt: Date.now() };
    setDraft(next);
    // Row-level commit: persist immediately (spec 07), if named.
    if (next.name.trim()) {
      void save(next).then(() => flashSaved());
    }
  };

  const flashSaved = (): void => {
    setSaved(true);
    setTimeout(() => setSaved(false), 1200);
  };

  const toggleFuelUnit = (unit: FuelUnit): void => {
    if (unit === draft.fuelUnit) return;
    // Preserve meaning, not digits (spec 07).
    const value = convertFuel(draft.fuelValue, draft.fuelUnit, unit, draft.cruiseKn);
    patch({ fuelUnit: unit, fuelValue: Number(value.toFixed(3)) });
  };

  const lPerNm = toFuelLPerNm(draft.fuelValue, draft.fuelUnit, draft.cruiseKn);

  return (
    <div className="sheet-backdrop" onClick={onClose}>
      <div className="sheet boat-form" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <div className="sheet__handle" />

        <label className="field">
          <span className="field__label">{msg.boat.name}</span>
          <input
            className="field__input"
            value={draft.name}
            maxLength={40}
            placeholder={msg.boat.namePlaceholder}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            onBlur={() => draft.name.trim() && void save({ ...draft, updatedAt: Date.now() }).then(flashSaved)}
          />
        </label>

        <label className="field">
          <span className="field__label">{msg.boat.draught}</span>
          <NumberField
            value={draft.draughtM}
            fractionDigits={1}
            min={0.1}
            max={5}
            step={0.1}
            suffix="m"
            onCommit={(v) => patch({ draughtM: v })}
          />
        </label>

        <label className="field">
          <span className="field__label">{msg.boat.cruise}</span>
          <NumberField
            value={draft.cruiseKn}
            fractionDigits={1}
            min={2}
            max={60}
            step={0.5}
            suffix="kn"
            onCommit={(v) => patch({ cruiseKn: v })}
          />
        </label>

        <label className="field">
          <span className="field__label">{msg.boat.clearance}</span>
          <NumberField
            value={draft.clearanceM}
            fractionDigits={1}
            min={0}
            max={2}
            step={0.1}
            suffix="m"
            onCommit={(v) => patch({ clearanceM: v })}
          />
        </label>

        <div className="field">
          <span className="field__label">{msg.boat.fuel}</span>
          <div className="field__fuel">
            <NumberField
              value={draft.fuelValue}
              fractionDigits={draft.fuelUnit === 'l_per_nm' ? 2 : 1}
              min={0}
              step={0.1}
              onCommit={(v) => patch({ fuelValue: v })}
            />
            <div className="segmented">
              <button
                type="button"
                className={`segmented__opt${draft.fuelUnit === 'l_per_nm' ? ' segmented__opt--on' : ''}`}
                onClick={() => toggleFuelUnit('l_per_nm')}
              >
                {msg.boat.unitPerNm}
              </button>
              <button
                type="button"
                className={`segmented__opt${draft.fuelUnit === 'l_per_h' ? ' segmented__opt--on' : ''}`}
                onClick={() => toggleFuelUnit('l_per_h')}
              >
                {msg.boat.unitPerH}
              </button>
            </div>
          </div>
        </div>

        <label className="field">
          <span className="field__label">
            {msg.boat.tank} <em className="field__hint">{msg.boat.tankOptional}</em>
          </span>
          <NumberField
            value={draft.tankL ?? 0}
            fractionDigits={0}
            min={0}
            step={5}
            suffix="l"
            onCommit={(v) => patch({ tankL: v > 0 ? v : null })}
          />
        </label>

        {draft.tankL != null && draft.tankL > 0 && (
          <div className="boat-form__range">
            {msg.boat.range}: <b className="numeral">{formatNumber(rangeNm(draft.tankL, lPerNm), 0)} nm</b>
          </div>
        )}

        <div className="boat-form__footer">
          {boat && (
            <button
              type="button"
              className="btn btn--ghost boat-form__delete"
              disabled={!canDelete}
              title={canDelete ? undefined : msg.boat.deleteBlocked}
              onClick={() => {
                void remove(draft.id);
                onClose();
              }}
            >
              {msg.boat.delete}
            </button>
          )}
          <button type="button" className="btn btn--accent" onClick={onClose}>
            {msg.boat.done}
          </button>
        </div>

        <div className={`boat-form__toast${saved ? ' boat-form__toast--on' : ''}`}>{msg.boat.saved}</div>
      </div>
    </div>
  );
}
